/**
 * MultiplayerClient - Socket.io basierter Multiplayer Client
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */

// Cache-Buster: v=20260119-9
import { GameRenderer } from './GameRenderer.js';
import { VirusRenderer } from './VirusRenderer.js';
import { Game, GameState } from './GameCore.js';
import { SessionStore } from './net/SessionStore.js';
import { TimerManager } from './net/TimerManager.js';
import { PillManager } from './net/PillManager.js';
import { InputValidator, VALIDATION } from './net/InputValidator.js';

// Debug-Konfiguration (false für Production)
const DEBUG_NETWORK = false;

// No-op Logging für Production
const log = DEBUG_NETWORK ? console.log.bind(console) : () => {};

// Dynamischer Import für ThreeRenderer (optional)
let ThreeRenderer = null;

export class MultiplayerClient {
    /**
     * @param {HTMLCanvasElement} canvas
     * @param {Object} options
     * @param {boolean} options.useThreeJS - Three.js Renderer verwenden (Standard: false)
     */
    constructor(canvas, options = {}) {
        this.socket = null;
        this.useThreeJS = options.useThreeJS || false;

        /** @type {boolean} Sperrt Klicks während ein Zug verarbeitet wird */
        this._movePending = false;
        /** @type {boolean} Render-Loop aktiv (false nach dispose) */
        this._renderLoopActive = false;

        // Renderer basierend auf Option wählen
        if (this.useThreeJS && typeof THREE !== 'undefined') {
            // ThreeRenderer lazy laden
            import('./ThreeRenderer.js')
                .then((module) => {
                    ThreeRenderer = module.ThreeRenderer;
                    this.renderer = new ThreeRenderer(canvas);
                    this.setupRendererCallbacks();
                    log('[MP] Using ThreeRenderer');
                })
                .catch((err) => {
                    console.warn('[MP] ThreeRenderer nicht verfügbar, fallback zu GameRenderer:', err);
                    this.renderer = new GameRenderer(canvas);
                    this.setupRendererCallbacks();
                });
        } else {
            this.renderer = new GameRenderer(canvas);
            log('[MP] Using GameRenderer');
        }
        this.virusRenderer = null;
        this.game = null;
        this.playerId = null;
        this.gameId = null;
        this.isReconnecting = false;

        // Tracking für Kettenreaktionen
        this.moveInProgress = false; // true während Kettenreaktion läuft
        this.moveStartFieldId = null; // Erstes Feld des aktuellen Zuges

        // Timer für verzögerte Kamera-Wiederherstellung
        this.cameraRestoreTimeout = null;

        // Session-Persistenz (sessionStorage, 1h TTL)
        this.session = new SessionStore();

        // Create virus overlay canvas
        this.setupVirusOverlay(canvas);

        // UI Callbacks
        this.onConnectionChange = null;
        this.onGamesListUpdate = null;
        this.onGameCreated = null;
        this.onGameJoined = null;
        this.onGameLeft = null;
        this.onPlayerJoined = null;
        this.onPlayerLeft = null;
        this.onPlayerUpdated = null;
        this.onGameStarted = null;
        this.onTurnChange = null;
        this.onFieldUpdate = null;
        this.onExplosion = null;
        this.onPlayerEliminated = null;
        this.onGameEnded = null;
        this.onChatMessage = null;
        this.onError = null;
        this.onReconnecting = null;
        this.onTurnTimeWarning = null;
        this.onTurnTimeout = null;
        this.onTurnTimeUpdate = null;
        this.onPillSpawned = null;
        this.onPillActivated = null;
        this.onAIAdded = null;
        this.onAIRemoved = null;
        this.onAIThinking = null;
        this.onAIMove = null;
        this.onPointsUpdate = null;
        this.onSlotsUpdated = null;
        this.onSlotConfigured = null;
        this.onCameraUpdate = null;
        this.onSpectateState = null;
        this.onSpectateError = null;
        this.onSpectatorCount = null;

        // Spectator mode flag
        this.isSpectating = false;
        this.spectatingGameId = null;

        // Kamera-Sync State
        this.cameraSyncEnabled = false;
        this.cameraFollowExplosions = true; // Standardmäßig aktiviert
        this.lastCameraState = null;
        this.cameraUpdateThrottle = null;
        this.CAMERA_UPDATE_INTERVAL = 80; // ms zwischen Updates

        // Pillen-Daten (fieldId -> Pill-Info)
        this.pills = new PillManager();

        // Timer-Anzeige (Turn-Countdown) — Callback wird dynamisch durchgereicht
        this.timer = new TimerManager((remaining, limit) => {
            this._invokeCallback('onTurnTimeUpdate', remaining, limit);
        });

        if (this.renderer && !this.useThreeJS) this.setupRendererCallbacks();
    }

    /**
     * Ruft einen UI-Callback sicher auf.
     * Prüft ob die Property eine Funktion ist, kapselt den Aufruf in try/catch
     * und lässt einen fehlerhaften UI-Handler den Socket-State nicht desynchronisieren.
     * Im Normalfall (Callback wirft nicht) identisches Verhalten wie ein direkter Aufruf.
     * @param {string} name - Name der Callback-Property (z.B. 'onFieldUpdate')
     * @param {...*} args - Argumente die an den Callback durchgereicht werden
     * @returns {*} Rückgabewert des Callbacks oder undefined (falls nicht gesetzt / Fehler)
     */
    _invokeCallback(name, ...args) {
        const cb = this[name];
        if (typeof cb !== 'function') {
            return undefined;
        }
        try {
            return cb.apply(this, args);
        } catch (err) {
            console.error(`[MP] Callback error (${name}):`, err?.message);
            return undefined;
        }
    }

    /**
     * Sicherer Emit mit Error-Handling
     * @param {string} event - Event-Name
     * @param {Object} data - Event-Daten
     * @returns {boolean} - true wenn emit erfolgreich
     */
    safeEmit(event, data = {}) {
        if (!this.socket) {
            console.warn(`[MP] Cannot emit '${event}': Socket not connected`);
            return false;
        }

        try {
            this.socket.emit(event, data);
            return true;
        } catch (error) {
            console.error(`[MP] Emit error for '${event}':`, error);
            return false;
        }
    }

    /**
     * Validiert und sanitized einen Spielernamen
     * @param {string} name - Eingabe-Name
     * @returns {string|null} - Bereinigter Name oder null bei Fehler
     */
    validatePlayerName(name) {
        return InputValidator.validatePlayerName(name);
    }

    /**
     * Validiert eine Game-ID
     * @param {string} gameId - Game-ID
     * @returns {boolean} - true wenn gültig
     */
    validateGameId(gameId) {
        return InputValidator.validateGameId(gameId);
    }

    /**
     * Validiert eine Color-ID
     * @param {string} colorId - Color-ID (z.B. 'red', 'blue')
     * @returns {boolean} - true wenn gültig
     */
    validateColorId(colorId) {
        return InputValidator.validateColorId(colorId);
    }

    /**
     * Validiert einen Virus-Typ
     * @param {string} virusType - Virus-Typ
     * @returns {boolean} - true wenn gültig
     */
    validateVirusType(virusType) {
        return InputValidator.validateVirusType(virusType);
    }

    /**
     * Session im Storage speichern
     */
    saveSession() {
        this.session.save(this.gameId, this.playerId);
    }

    /**
     * Session aus Storage laden
     */
    loadSession() {
        return this.session.load();
    }

    /**
     * Session löschen
     */
    clearSession() {
        this.session.clear();
    }

    /**
     * Prüft ob eine Session existiert und versucht Reconnect
     */
    async tryReconnect() {
        const session = this.loadSession();
        if (!session) {
            return false;
        }

        log('[MP] Found session, attempting reconnect...');
        this.isReconnecting = true;

        return new Promise((resolve) => {
            this.safeEmit('game:reconnect', {
                gameId: session.gameId,
                playerId: session.playerId
            });

            // Timeout für Reconnect
            const timeout = setTimeout(() => {
                log('[MP] Reconnect timeout');
                this.isReconnecting = false;
                this.clearSession();
                this.socket.off('game:reconnected', handler);
                this.socket.off('game:reconnectFailed', failHandler);
                resolve(false);
            }, 5000);

            // Einmaliger Handler für Reconnect-Response
            const handler = (data) => {
                clearTimeout(timeout);
                this.isReconnecting = false;

                if (data.success) {
                    log('[MP] Reconnect successful');
                    resolve(true);
                } else {
                    log('[MP] Reconnect failed:', data.error);
                    this.clearSession();
                    resolve(false);
                }
            };

            const failHandler = (_data) => {
                clearTimeout(timeout);
                this.isReconnecting = false;
                this.clearSession();
                this.socket.off('game:reconnected', handler);
                resolve(false);
            };

            this.socket.once('game:reconnected', handler);
            this.socket.once('game:reconnectFailed', failHandler);
        });
    }

    /**
     * Verbindet zum Server
     */
    connect() {
        // Idempotent: ist bereits ein Socket vorhanden, NICHT erneut io() aufrufen —
        // socket.io liefert für dieselbe URL/Namespace den gecachten Socket zurück,
        // und setupSocketEvents() würde dann alle game:*-Handler ein zweites Mal binden
        // (doppelte Feld-Updates/Explosionen/Turn-Wechsel). Stattdessen den bestehenden
        // Socket wiederverbinden — alle Listener (auch die auf Landing-Ebene einmalig
        // gebundenen wie chat:reaction/notification/matchmaking) bleiben erhalten.
        if (this.socket) {
            return new Promise((resolve, reject) => {
                if (this.socket.connected) {
                    resolve();
                    return;
                }
                const onConnect = () => {
                    this.socket.off('connect_error', onError);
                    resolve();
                };
                const onError = (err) => {
                    this.socket.off('connect', onConnect);
                    reject(err);
                };
                this.socket.once('connect', onConnect);
                this.socket.once('connect_error', onError);
                this.socket.connect();
            });
        }

        return new Promise((resolve, reject) => {
            let settled = false;
            this.socket = io({
                transports: ['polling'],
                reconnection: true,
                reconnectionAttempts: 5,
                reconnectionDelay: 1000
            });

            const connectErrorHandler = (err) => {
                console.error('[MP] Connection error:', err);
                this._invokeCallback('onConnectionChange', 'error');
                if (!settled) {
                    settled = true;
                    reject(err);
                }
            };

            this.socket.on('connect', async () => {
                log('[MP] Connected:', this.socket.id);
                this._invokeCallback('onConnectionChange', 'connected');

                // Bei Reconnect nach Socket-Disconnect
                if (this.gameId && this.playerId) {
                    log('[MP] Socket reconnected, rejoining game...');
                    this.safeEmit('game:reconnect', {
                        gameId: this.gameId,
                        playerId: this.playerId
                    });
                }

                if (!settled) {
                    settled = true;
                    this.socket.off('connect_error', connectErrorHandler);
                    resolve();
                }
            });

            this.socket.on('disconnect', () => {
                log('[MP] Disconnected');

                // Reset spectator state on disconnect
                this.isSpectating = false;
                this.spectatingGameId = null;

                this._invokeCallback('onConnectionChange', 'disconnected');
            });

            this.socket.on('connect_error', connectErrorHandler);

            this.setupSocketEvents();
        });
    }

    setupSocketEvents() {
        // Spieleliste
        this.socket.on('games:list', (data) => {
            this._invokeCallback('onGamesListUpdate', data.games);
        });

        this.socket.on('games:updated', () => {
            this.requestGamesList();
        });

        // Spiel erstellt
        this.socket.on('game:created', (data) => {
            if (data.success) {
                this.gameId = data.gameId;
                this.playerId = data.playerId;
                this.initGameFromData(data.game);
                this.saveSession();
                this._invokeCallback('onGameCreated', data);
            } else {
                this._invokeCallback('onError', 'create_failed', data.error);
            }
        });

        // Spiel beigetreten
        this.socket.on('game:joined', (data) => {
            if (data.success) {
                this.gameId = data.gameId;
                this.playerId = data.playerId;
                this.initGameFromData(data.game);
                this.saveSession();
                this._invokeCallback('onGameJoined', data);
            } else {
                this._invokeCallback('onError', 'join_failed', data.error);
            }
        });

        // Spiel verlassen
        this.socket.on('game:left', () => {
            this.game = null;
            this.gameId = null;
            this.playerId = null;
            this.clearSession();

            // Kamera-Timer aufräumen
            if (this.cameraRestoreTimeout) {
                clearTimeout(this.cameraRestoreTimeout);
                this.cameraRestoreTimeout = null;
            }

            this._invokeCallback('onGameLeft');
        });

        // Spieler beigetreten
        this.socket.on('player:joined', (data) => {
            if (this.game) {
                // Spieler zur lokalen Kopie hinzufügen
                const _player = this.game.addPlayer(data.player);
                this.renderer.buildGeometry();
                this._invokeCallback('onPlayerJoined', data.player);
            }
        });

        // Spieler verlassen
        this.socket.on('player:left', (data) => {
            if (this.game) {
                this.game.removePlayer(data.playerId);
                this.renderer.buildGeometry();
                this._invokeCallback('onPlayerLeft', data);
            }
        });

        // Spieler aktualisiert
        this.socket.on('player:updated', (data) => {
            if (this.game) {
                const player = this.game.players.get(data.playerId);
                if (player) {
                    player.deserialize(data.player);
                }
                this._invokeCallback('onPlayerUpdated', data);
            }
        });

        // Spieler disconnected/reconnected
        this.socket.on('player:disconnected', (data) => {
            if (this.game) {
                const player = this.game.players.get(data.playerId);
                if (player) {
                    player.isConnected = false;
                }
                this._invokeCallback('onPlayerUpdated', { playerId: data.playerId, disconnected: true });
            }
        });

        this.socket.on('player:reconnected', (data) => {
            if (this.game) {
                const player = this.game.players.get(data.playerId);
                if (player) {
                    player.isConnected = true;
                }
                this._invokeCallback('onPlayerUpdated', { playerId: data.playerId, reconnected: true });
            }
        });

        // Spiel gestartet
        this.socket.on('game:started', (data) => {
            if (data && data.success) {
                this.initGameFromData(data.game);

                // Clickable fields für ersten Spieler setzen
                const currentPlayer = this.game.getCurrentPlayer();
                if (currentPlayer && currentPlayer.id === this.playerId) {
                    // Erster Spieler in Runde 1 ohne eigene Felder = Platzierungsphase
                    const playerFields = this.game.sphere.getPlayerFields(this.playerId);
                    const isPlacementPhase = this.game.currentRound === 1 && playerFields.length === 0;
                    this.renderer.updateClickableFields(this.playerId, isPlacementPhase);
                } else {
                    this.renderer.updateClickableFields(null);
                }

                this._invokeCallback('onGameStarted', data.game);
            } else {
                this._invokeCallback('onError', 'start_failed', data?.error);
            }
        });

        // Zug-Ergebnis
        this.socket.on('game:moveResult', (data) => {
            // Klick-Sperre aufheben (egal ob Erfolg oder Fehler)
            this._movePending = false;

            if (!data.success) {
                this._invokeCallback('onError', 'move_failed', data.error);
            }
        });

        // Feld aktualisiert
        this.socket.on('game:fieldUpdate', (data) => {
            log(
                '[MP] game:fieldUpdate received:',
                data.field.id,
                'value:',
                data.field.value,
                'owner:',
                data.field.ownerId
            );
            if (this.game) {
                const field = this.game.sphere.getField(data.field.id);
                // Alte Werte VOR dem Update speichern
                let oldValue = 0;
                let oldOwner = null;

                if (field) {
                    oldValue = field.value;
                    oldOwner = field.ownerId;
                    field.deserialize(data.field);
                    log(
                        '[MP] Field updated:',
                        field.id,
                        'old:',
                        oldValue,
                        '->',
                        field.value,
                        'owner:',
                        oldOwner,
                        '->',
                        field.ownerId
                    );
                    this.renderer.updateColors();
                    this.renderer.updatePills();

                    // Kamera-Logik: Bei erstem Feld eines Zuges entscheiden
                    if (data.field.value > oldValue && !this.moveInProgress && this.renderer) {
                        this.moveInProgress = true;
                        this.moveStartFieldId = data.field.id;

                        const owner = this.game.players.get(data.field.ownerId);
                        const isAIMove = owner?.isAI === true;
                        const isOwnMove = data.field.ownerId === this.playerId;
                        const _isHumanOpponent = !isAIMove && !isOwnMove;

                        // Debug-Logging für AI-Move Erkennung
                        log(
                            '[Camera Debug] ownerId:',
                            data.field.ownerId,
                            'owner:',
                            owner?.name,
                            'isAI:',
                            owner?.isAI,
                            'playersInMap:',
                            [...this.game.players.keys()]
                        );

                        // NEUE KAMERA-LOGIK:
                        // - "Kamera folgen" AUS: Immer zum Feld rotieren (außer eigener Zug)
                        // - "Kamera folgen" AN + Mensch: Kamera wird via camera:update synchronisiert
                        // - "Kamera folgen" AN + KI: Zum Feld rotieren
                        // - Eigener Zug: Nie automatisch rotieren

                        // Kamera-Rotation wird zentral von landing.js gesteuert
                        // Hier KEINE automatischen rotateToField-Aufrufe
                    }
                } else {
                    console.warn('[MP] Field not found:', data.field.id);
                }

                this._invokeCallback('onFieldUpdate', data.field, { oldValue, oldOwner });
            }
        });

        // Explosion
        this.socket.on('game:explosion', (data) => {
            // Explosion visuell darstellen
            if (this.renderer && data.fieldId) {
                // Explosions-Effekt auf dem explodierenden Feld (WebGL Shader)
                this.renderer.triggerExplosion(data.fieldId);

                // Spielerfarbe holen für Effekte
                const owner = this.game?.players.get(data.owner);
                const color = owner?.color?.hex || '#888';
                const _isOwnExplosion = data.owner === this.playerId;

                // Kamera-Rotation bei Explosionen wird von landing.js gesteuert

                // Explosions-Partikel im VirusRenderer (Canvas Overlay)
                if (this.virusRenderer) {
                    this.virusRenderer.triggerExplosion(data.fieldId, color);

                    // Werterhöhungs-Effekt auf Nachbarfeldern
                    if (data.affectedNeighbors && Array.isArray(data.affectedNeighbors)) {
                        for (const neighborId of data.affectedNeighbors) {
                            // Verzögerter Effekt für gestaffeltes Aussehen
                            setTimeout(
                                () => {
                                    if (this.virusRenderer) {
                                        this.virusRenderer.triggerValueIncrease(neighborId, color, data.fieldId);
                                    }
                                },
                                50 + Math.random() * 100
                            );
                        }
                    }
                }
            }

            this._invokeCallback('onExplosion', data);
        });

        // Batched Tick-Update für Kettenreaktionen (Performance-optimiert)
        // Verarbeitet alle Feld-Updates eines Ticks in einem Event
        this.socket.on('game:tickComplete', (data) => {
            if (!this.game) return;

            // Alle Felder auf einmal aktualisieren
            for (const fieldData of data.fieldUpdates) {
                const field = this.game.sphere.getField(fieldData.id);
                if (field) {
                    field.deserialize(fieldData);
                }
            }

            // Explosions-Effekte für alle explodierenden Felder
            for (const fieldId of data.explosions) {
                if (this.renderer) {
                    this.renderer.triggerExplosion(fieldId);
                }

                // Partikel-Effekte
                if (this.virusRenderer) {
                    const field = this.game.sphere.getField(fieldId);
                    const owner = field ? this.game.players.get(field.ownerId) : null;
                    const color = owner?.color?.hex || '#888';
                    this.virusRenderer.triggerExplosion(fieldId, color);
                }
            }

            // Farben und Pillen nur einmal am Ende updaten (statt für jedes Feld einzeln)
            if (this.renderer) {
                this.renderer.updateColors();
                this.renderer.updatePills();
            }

            // Callback für externe Handler (z.B. Combo-Counter, Sounds)
            this._invokeCallback('onTickComplete', data);
        });

        // Zug-Wechsel (kommt erst NACH allen Kettenreaktionen)
        this.socket.on('game:turnChange', (data) => {
            if (this.game) {
                // Kettenreaktion beendet - Reset für nächsten Zug
                this.moveInProgress = false;
                this._movePending = false; // Klick-Sperre aufheben
                this.moveStartFieldId = null;

                // Hinweis: Kameraposition wird VOR dem Zug in onFieldClick gespeichert,
                // damit sie nicht durch Kettenreaktions-Rotationen verfälscht wird

                this.game.currentRound = data.round;
                // Aktualisiere currentPlayerIndex
                const idx = this.game.playerOrder.indexOf(data.currentPlayerId);
                if (idx !== -1) {
                    this.game.currentPlayerIndex = idx;
                }

                // Timer-Daten aktualisieren
                this.timer.start(data.turnTimeLimit || 0, data.turnStartTime);

                // Clickable fields aktualisieren wenn wir dran sind
                if (data.currentPlayerId === this.playerId) {
                    // Kamera bleibt wo sie ist - keine automatische Wiederherstellung
                    // (Bessere Orientierung: User sieht wo die letzte Aktion war)
                    if (this.cameraRestoreTimeout) {
                        clearTimeout(this.cameraRestoreTimeout);
                        this.cameraRestoreTimeout = null;
                    }

                    // Platzierungsphase: Runde 1 und Spieler hat noch keine Felder
                    const playerFields = this.game.sphere.getPlayerFields(this.playerId);
                    const isPlacementPhase = data.round === 1 && playerFields.length === 0;
                    this.renderer.updateClickableFields(this.playerId, isPlacementPhase);
                } else {
                    this.renderer.updateClickableFields(null);
                }

                this._invokeCallback('onTurnChange', data);
            }
        });

        // Timer-Warnung (X Sekunden vor Ablauf)
        this.socket.on('game:turnTimeWarning', (data) => {
            this._invokeCallback('onTurnTimeWarning', data);
        });

        // Timer abgelaufen
        this.socket.on('game:turnTimeout', (data) => {
            this.timer.stop();
            this._invokeCallback('onTurnTimeout', data);
        });

        // Pille gespawnt
        this.socket.on('game:pillSpawned', (data) => {
            if (data.pill) {
                this.pills.addPill(data.pill.fieldId, data.pill);
                // Pille auf lokalem Feld setzen
                if (this.game) {
                    const field = this.game.sphere.getField(data.pill.fieldId);
                    if (field) {
                        field.pill = data.pill;
                        // Renderer aktualisieren
                        if (this.renderer) {
                            this.renderer.updateField(field);
                        }
                    }
                }
                this._invokeCallback('onPillSpawned', data.pill);
            }
        });

        // Pille aktiviert
        this.socket.on('game:pillActivated', (data) => {
            // Pille aus lokaler Liste und vom Feld entfernen
            this.pills.removePill(data.fieldId);
            if (this.game) {
                const field = this.game.sphere.getField(data.fieldId);
                if (field) {
                    field.pill = null;
                    // Renderer aktualisieren (Pillen-Buffer!)
                    if (this.renderer) {
                        this.renderer.updatePills();
                    }
                }
            }
            this._invokeCallback('onPillActivated', data);
        });

        // KI-Spieler hinzugefügt
        this.socket.on('game:aiAdded', (data) => {
            if (data.success && this.game) {
                // Player wird bereits über player:joined hinzugefügt
                this._invokeCallback('onAIAdded', data);
            } else {
                this._invokeCallback('onError', 'ai_add_failed', data.error);
            }
        });

        // KI-Spieler entfernt
        this.socket.on('game:aiRemoved', (data) => {
            if (data.success) {
                this._invokeCallback('onAIRemoved', data);
            } else {
                this._invokeCallback('onError', 'ai_remove_failed', data.error);
            }
        });

        // KI denkt nach (für "Denkt nach..." Anzeige und Kamerafahrt zum Zielfeld)
        this.socket.on('game:aiThinking', (data) => {
            log(
                '[MP] game:aiThinking received:',
                data.playerName,
                'thinking for',
                Math.round(data.thinkTime),
                'ms',
                'targetField:',
                data.targetFieldId
            );

            // Kamera zum Zielfeld der KI fahren (VOR dem eigentlichen Zug)
            // Kamera-Rotation wird von landing.js via onAIThinking gesteuert

            this._invokeCallback('onAIThinking', data);
        });

        // KI-Zug (für visuelle Hervorhebung und Kamerafahrt)
        this.socket.on('game:aiMove', (data) => {
            log('[MP] game:aiMove received:', data.playerId, 'field:', data.fieldId);

            // Kamera-Rotation wird von landing.js via onAIMove gesteuert

            this._invokeCallback('onAIMove', data);
        });

        // Punkte-Update
        this.socket.on('game:pointsUpdate', (data) => {
            log('[MP] game:pointsUpdate received:', data.playerId, '+', data.change, 'points', 'stats:', data.stats);

            // Lokalen Spieler-Punktestand und Stats aktualisieren
            if (this.game) {
                const player = this.game.players.get(data.playerId);
                if (player) {
                    player.points = data.points;
                    // Stats aktualisieren (explosionsCaused, fieldsConquered, etc.)
                    if (data.stats) {
                        player.stats = { ...player.stats, ...data.stats };
                    }
                }
            }

            this._invokeCallback('onPointsUpdate', data);
        });

        // Slot konfiguriert
        this.socket.on('game:slotConfigured', (data) => {
            if (data.success && this.game && data.slot) {
                // Lokale Slots aktualisieren
                const idx = this.game.slots?.findIndex((s) => s.index === data.slot.index);
                if (idx >= 0) {
                    this.game.slots[idx] = { ...data.slot };
                }
            }
            this._invokeCallback('onSlotConfigured', data);
        });

        // Slots aktualisiert
        this.socket.on('game:slotsUpdated', (data) => {
            if (this.game) {
                this.game.slots = data.slots;
                this.game.canStartResult = data.canStart;
            }
            this._invokeCallback('onSlotsUpdated', data);
        });

        // Spieler eliminiert
        this.socket.on('game:playerEliminated', (data) => {
            if (this.game) {
                const player = this.game.players.get(data.playerId);
                if (player) {
                    player.eliminate();
                    this.renderer.updateColors();
                    this.renderer.updatePills();
                }
                this._invokeCallback('onPlayerEliminated', data);
            }
        });

        // Spiel beendet
        this.socket.on('game:ended', (data) => {
            if (this.game) {
                this.game.state = GameState.FINISHED;
                this.game.winner = data.winner;
            }
            // Session löschen wenn Spiel beendet
            this.clearSession();

            // Verzögerung damit die letzte Kettenreaktion visuell abgeschlossen werden kann
            // bevor das Winner-Overlay erscheint
            const GAME_END_DELAY_MS = 2000;
            setTimeout(() => {
                this._invokeCallback('onGameEnded', data);
            }, GAME_END_DELAY_MS);
        });

        // Zustandsänderung
        this.socket.on('game:stateChange', (data) => {
            if (this.game) {
                this.game.state = data.state;
            }
        });

        // Chat
        this.socket.on('chat:message', (data) => {
            this._invokeCallback('onChatMessage', data);
        });

        this.socket.on('chat:quickchat', (data) => {
            this._invokeCallback('onChatMessage', {
                ...data,
                isQuickChat: true
            });
        });

        // Kamera-Update von anderem Spieler
        this.socket.on('camera:update', (data) => {
            // "Kamera folgen" Logik:
            // Nur anwenden wenn:
            // 1. Sync aktiviert ist
            // 2. Wir NICHT selbst dran sind
            // 3. Der aktuelle Spieler ein MENSCH ist (nicht KI)
            if (this.cameraSyncEnabled && this.renderer && this.game) {
                const currentPlayer = this.game.getCurrentPlayer();
                const isOurTurn = currentPlayer?.id === this.playerId;
                const isHumanPlayer = currentPlayer && !currentPlayer.isAI;

                // Nur bei menschlichen Gegnern die Kamera synchronisieren
                if (!isOurTurn && isHumanPlayer && data.playerId === currentPlayer?.id) {
                    log('[Camera] Syncing to player', currentPlayer.name);
                    this.renderer.setCameraState(
                        {
                            rotation: data.rotation,
                            zoom: data.zoom
                        },
                        true
                    ); // Mit Animation
                }
            }
            this._invokeCallback('onCameraUpdate', data);
        });

        // State Sync (z.B. nach Undo)
        this.socket.on('game:stateSync', (data) => {
            if (data?.game) {
                log(`[MP] State sync: reason=${data.reason}, by=${data.undoBy || 'server'}`);
                this.initGameFromData(data.game);
                if (this.renderer) {
                    this.renderer.updateColors();
                    this.renderer.updatePills();
                }
                this._invokeCallback('onGameStateChange', data.game);
            }
        });

        // Undo-Ergebnis
        this.socket.on('game:undoResult', (data) => {
            this._invokeCallback('onUndoResult', data);
        });

        // Reconnect
        this.socket.on('game:reconnected', (data) => {
            if (data.success) {
                this.gameId = data.game.id;
                this.playerId = data.playerId;
                this.initGameFromData(data.game);
                this.saveSession();
                this._invokeCallback('onGameJoined', { ...data, isReconnect: true });
            }
        });

        // Reconnect fehlgeschlagen
        this.socket.on('game:reconnectFailed', (data) => {
            log('[MP] Reconnect failed:', data.error);
            this.clearSession();
        });

        // =====================================================================
        // SPECTATOR MODE EVENTS
        // =====================================================================

        // Spectator state received — initialize read-only game view
        this.socket.on('game:spectateState', (data) => {
            this.isSpectating = true;
            this.spectatingGameId = data.gameId;

            // Build a game-like data structure for initGameFromData
            const gameData = {
                id: data.gameId,
                state: data.state,
                config: data.config,
                currentRound: data.currentRound,
                currentPlayerId: data.currentPlayerId,
                players: data.players,
                fields: data.fields,
                pills: data.pills || [],
                turnTimeLimit: data.turnTimeLimit,
                turnStartTime: data.turnStartTime,
                hostId: data.players?.[0]?.id || null,
                slots: []
            };

            this.initGameFromData(gameData);

            // Spectators cannot click fields
            this.renderer.updateClickableFields(null);

            this._invokeCallback('onSpectateState', data);

            log('[MP] Spectating game:', data.gameId);
        });

        // Spectator error
        this.socket.on('game:spectateError', (data) => {
            this._invokeCallback('onSpectateError', data);
        });

        // Spectator count update
        this.socket.on('game:spectatorCount', (data) => {
            this._invokeCallback('onSpectatorCount', data);
        });
    }

    /**
     * Erstellt Virus-Overlay Canvas
     */
    setupVirusOverlay(canvas) {
        // Overlay Canvas erstellen
        const overlay = document.createElement('canvas');
        overlay.id = 'virus-overlay';
        overlay.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 1;
        `;

        // Ins Container einfuegen
        if (canvas.parentNode) {
            canvas.parentNode.style.position = 'relative';
            canvas.parentNode.appendChild(overlay);
        }

        this.virusOverlay = overlay;
        this.virusRenderer = new VirusRenderer(overlay, this.renderer);
    }

    setupRendererCallbacks() {
        this._movePending = false; // Verhindert Doppelklick

        this.renderer.onFieldClick = (fieldId) => {
            if (this.game && this.socket && !this._movePending) {
                const currentPlayer = this.game.getCurrentPlayer();
                if (currentPlayer && currentPlayer.id === this.playerId) {
                    // Klick-Sperre: Verhindert zweiten Zug bevor Server antwortet
                    this._movePending = true;

                    // Kameraposition VOR dem Zug speichern
                    if (this.renderer) {
                        this.renderer.savePlayerCamera(this.playerId);
                    }
                    this.safeEmit('game:move', { fieldId });

                    // Haptic feedback
                    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches && navigator.vibrate) {
                        navigator.vibrate(30);
                    }

                    // Safety: Sperre nach 5s automatisch aufheben (falls moveResult verloren geht)
                    setTimeout(() => { this._movePending = false; }, 5000);
                }
            }
        };

        this.renderer.onFieldHover = (_fieldId) => {
            // Optional: Hover-Info anzeigen
        };

        // Kamera-Änderungen broadcasten wenn wir am Zug sind
        this.renderer.onCameraChange = () => {
            this.broadcastCameraUpdate();
        };
    }

    /**
     * Initialisiert lokale Spielkopie aus Server-Daten
     */
    initGameFromData(gameData) {
        log('[Game] initGameFromData:', gameData);
        log('[Game] Config:', gameData.config);
        log('[Game] Fields from server:', gameData.fields?.length);

        this.game = new Game(gameData.config);
        this.game.id = gameData.id;
        this.game.state = gameData.state;
        this.game.currentRound = gameData.currentRound;
        this.game.hostId = gameData.hostId;

        log('[Game] Client sphere fields:', this.game.sphere.fields.size);
        log('[Game] First field:', this.game.sphere.fields.get(0));

        // Spieler hinzufügen
        this.game.players.clear();
        this.game.playerOrder = [];
        for (const pData of gameData.players) {
            log('[Game] Adding player from server:', pData.id, pData.name, 'isAI:', pData.isAI);
            const player = this.game.addPlayer(pData);
            if (player) {
                player.deserialize(pData);
                log('[Game] Player added:', player.id, player.name, 'isAI after add:', player.isAI);
            }
        }

        // CurrentPlayerIndex setzen basierend auf currentPlayerId
        if (gameData.currentPlayerId) {
            const idx = this.game.playerOrder.indexOf(gameData.currentPlayerId);
            if (idx !== -1) {
                this.game.currentPlayerIndex = idx;
            }
        }

        // Slots übernehmen
        if (gameData.slots) {
            this.game.slots = gameData.slots;
        }

        // canStart übernehmen
        if (gameData.canStart) {
            this.game.canStartResult = gameData.canStart;
        }

        // Felder aktualisieren
        if (gameData.fields) {
            this.game.sphere.deserializeFields(gameData.fields);
        }

        // Debug: Check field structure
        const testField = this.game.sphere.fields.get(0);
        if (testField) {
            log('[Game] Field 0 neighbors:', testField.neighbors);
            log('[Game] Field 0 center:', testField.center);
            log('[Game] Field 0 centerNormalized:', testField.centerNormalized);
        }

        // Renderer aktualisieren
        log('[Game] Setting game on renderer...');
        this.renderer.setGame(this.game);
        log('[Game] Renderer vertex count:', this.renderer.vertexCount);

        // Virus Renderer aktualisieren
        if (this.virusRenderer) {
            this.virusRenderer.setGame(this.game);
        }

        // Pillen laden und auf Felder setzen
        this.pills.clear();
        if (gameData.pills && gameData.pills.length > 0) {
            for (const pill of gameData.pills) {
                this.pills.addPill(pill.fieldId, pill);
                // Pille auch auf lokalem Feld setzen für Rendering
                const field = this.game.sphere.getField(pill.fieldId);
                if (field) {
                    field.pill = pill;
                }
            }
            // Pillen-Buffer aktualisieren (nach setGame)
            this.renderer.updatePills();
        }

        // Timer-Daten initialisieren
        if (gameData.turnTimeLimit) {
            this.timer.start(gameData.turnTimeLimit, gameData.turnStartTime);
        }

        // Clickable fields wenn wir dran sind
        if (gameData.currentPlayerId === this.playerId) {
            // Platzierungsphase: Runde 1 und Spieler hat noch keine Felder
            const playerFields = this.game.sphere.getPlayerFields(this.playerId);
            const isPlacementPhase = gameData.currentRound === 1 && playerFields.length === 0;
            this.renderer.updateClickableFields(this.playerId, isPlacementPhase);
        }
    }

    /**
     * Spieleliste anfordern
     */
    requestGamesList() {
        this.safeEmit('games:list');
    }

    /**
     * Start spectating a game
     * @param {string} gameId
     * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
     */
    spectateGame(gameId) {
        if (!gameId || typeof gameId !== 'string') return false;
        if (this.isSpectating && this.spectatingGameId === gameId) return false;
        return this.safeEmit('game:spectate', { gameId });
    }

    /**
     * Stop spectating — cleans up all spectator state and notifies server
     * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
     */
    stopSpectating() {
        this.isSpectating = false;
        this.spectatingGameId = null;
        this.game = null;

        // Stop turn timer if running
        this.timer.stop();

        // Clear camera timers
        if (this.cameraRestoreTimeout) {
            clearTimeout(this.cameraRestoreTimeout);
            this.cameraRestoreTimeout = null;
        }

        // Reset renderer to idle state
        if (this.renderer) {
            this.renderer.updateClickableFields(null);
        }

        this.safeEmit('game:stopSpectating');
    }

    /**
     * Spiel erstellen
     */
    createGame(options) {
        // Spielername validieren
        const playerName = this.validatePlayerName(options.playerName);
        if (!playerName) {
            console.error('[MP] createGame: Invalid playerName');
            return false;
        }

        if (!this.validateColorId(options.colorId)) {
            console.error('[MP] createGame: Invalid colorId');
            return false;
        }

        if (!this.validateVirusType(options.virusType)) {
            console.error('[MP] createGame: Invalid virusType');
            return false;
        }

        return this.safeEmit('game:create', {
            playerName,
            colorId: options.colorId,
            virusType: options.virusType,
            subdivisions: options.subdivisions || 3,
            maxPlayers: options.maxPlayers || 4,
            isPublic: options.isPublic !== false,
            password: options.password || null,
            pillsEnabled: options.pillsEnabled || false,
            turnTimeLimit: options.turnTimeLimit || 0,
            randomFill: options.randomFill || false,
            randomFillMin: options.randomFillMin ?? 1,
            randomFillMax: options.randomFillMax ?? 3,
            slots: options.slots || null, // Slot-Konfiguration
            mapPreset: options.mapPreset || 'classic',
            gameMode: options.gameMode || 'standard',
            blockedFields: Array.isArray(options.blockedFields) ? options.blockedFields : null
        });
    }

    /**
     * Spiel beitreten
     * @param {string} gameId - Game-ID
     * @param {Object} options - Spieler-Optionen
     * @returns {boolean} - true wenn Request gesendet wurde
     */
    joinGame(gameId, options) {
        // Validierung
        if (!this.validateGameId(gameId)) {
            console.error('[MP] joinGame: Invalid gameId');
            return false;
        }

        const playerName = this.validatePlayerName(options.playerName);
        if (!playerName) {
            console.error('[MP] joinGame: Invalid playerName');
            return false;
        }

        if (!this.validateColorId(options.colorId)) {
            console.error('[MP] joinGame: Invalid colorId');
            return false;
        }

        if (!this.validateVirusType(options.virusType)) {
            console.error('[MP] joinGame: Invalid virusType');
            return false;
        }

        return this.safeEmit('game:join', {
            gameId,
            playerName,
            colorId: options.colorId,
            virusType: options.virusType,
            password: options.password || null
        });
    }

    /**
     * Spiel verlassen
     */
    leaveGame() {
        this.safeEmit('game:leave');
    }

    /**
     * Letzten Zug rückgängig machen (einmal pro Spiel)
     */
    undoLastMove() {
        this.safeEmit('game:undo');
    }

    /**
     * Alle Ressourcen aufräumen (Memory Leak Prevention)
     * Sollte aufgerufen werden wenn der Client nicht mehr benötigt wird
     */
    dispose() {
        // Render-Loop stoppen (verhindert Null-Zugriffe nach dispose)
        this._renderLoopActive = false;

        // Socket trennen
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }

        // Renderer aufräumen
        if (this.renderer && typeof this.renderer.dispose === 'function') {
            this.renderer.dispose();
        }
        this.renderer = null;

        // VirusRenderer aufräumen
        if (this.virusRenderer && typeof this.virusRenderer.destroy === 'function') {
            this.virusRenderer.destroy();
        }
        this.virusRenderer = null;
        if (this.virusOverlay && this.virusOverlay.parentNode) {
            this.virusOverlay.parentNode.removeChild(this.virusOverlay);
        }
        this.virusOverlay = null;

        // Timer aufräumen
        if (this.timer) {
            this.timer.dispose();
        }
        if (this.cameraRestoreTimeout) {
            clearTimeout(this.cameraRestoreTimeout);
            this.cameraRestoreTimeout = null;
        }
        if (this.cameraUpdateThrottle) {
            clearTimeout(this.cameraUpdateThrottle);
            this.cameraUpdateThrottle = null;
        }

        // Session aufräumen
        this.clearSession();

        // Referenzen aufräumen
        this.game = null;
        this.playerId = null;
        this.gameId = null;

        console.info('[MultiplayerClient] Disposed successfully');
    }

    /**
     * Spiel starten (nur Host)
     */
    startGame() {
        this.safeEmit('game:start');
    }

    /**
     * Spielerdaten aktualisieren
     */
    updatePlayer(data) {
        this.safeEmit('player:update', data);
    }

    /**
     * KI-Spieler hinzufügen (nur Host)
     * @param {Object} config - { difficulty: 'easy'|'normal'|'hard', name?: string, virusType?: string }
     */
    addAIPlayer(config = {}) {
        this.safeEmit('game:addAI', {
            difficulty: config.difficulty || 'normal',
            name: config.name,
            virusType: config.virusType
        });
    }

    /**
     * KI-Spieler entfernen (nur Host)
     * @param {string} playerId - ID des zu entfernenden KI-Spielers
     */
    removeAIPlayer(playerId) {
        this.safeEmit('game:removeAI', { playerId });
    }

    /**
     * Slot konfigurieren (nur Host)
     * @param {number} slotIndex - Slot-Index (1-5, 0 ist Host)
     * @param {Object} config - { type: 'player'|'ai'|'blocked', colorId?, aiDifficulty? }
     */
    configureSlot(slotIndex, config) {
        this.safeEmit('game:configureSlot', {
            slotIndex,
            type: config.type,
            colorId: config.colorId,
            aiDifficulty: config.aiDifficulty
        });
    }

    /**
     * Chat-Nachricht senden
     */
    sendChatMessage(text) {
        if (text && text.trim()) {
            // Client-seitige Längenprüfung
            const trimmed = text.trim().substring(0, VALIDATION.MAX_MESSAGE_LENGTH);
            this.safeEmit('chat:message', { text: trimmed });
        }
    }

    /**
     * Quick-Chat senden
     */
    sendQuickChat(emote) {
        this.safeEmit('chat:quickchat', { emote });
    }

    /**
     * Prüft ob lokaler Spieler Host ist
     */
    isHost() {
        return this.game && this.game.hostId === this.playerId;
    }

    /**
     * Prüft ob lokaler Spieler am Zug ist
     */
    isMyTurn() {
        if (!this.game) return false;
        const current = this.game.getCurrentPlayer();
        return current && current.id === this.playerId;
    }

    /**
     * Kamera-Sync aktivieren/deaktivieren
     * @param {boolean} enabled
     */
    setCameraSyncEnabled(enabled) {
        this.cameraSyncEnabled = enabled;
        log('[Camera] Sync', enabled ? 'enabled' : 'disabled');
    }

    /**
     * Prüft ob Kamera-Sync aktiviert ist
     */
    isCameraSyncEnabled() {
        return this.cameraSyncEnabled;
    }

    /**
     * Kamera-Follow-Explosions aktivieren/deaktivieren
     * @param {boolean} enabled
     */
    setCameraFollowExplosions(enabled) {
        this.cameraFollowExplosions = enabled;
        log('[Camera] Follow explosions', enabled ? 'enabled' : 'disabled');
    }

    /**
     * Sendet aktuelle Kamera-Position an andere Spieler (throttled)
     * Wird aufgerufen wenn der lokale Spieler die Kamera bewegt
     */
    broadcastCameraUpdate() {
        // Nur wenn wir am Zug sind
        if (!this.isMyTurn() || !this.socket || !this.renderer) return;

        // Throttling
        if (this.cameraUpdateThrottle) return;

        const state = this.renderer.getCameraState();

        // Nur senden wenn sich etwas geändert hat
        if (this.lastCameraState) {
            const rotDiff =
                Math.abs(state.rotation[0] - this.lastCameraState.rotation[0]) +
                Math.abs(state.rotation[1] - this.lastCameraState.rotation[1]) +
                Math.abs(state.rotation[2] - this.lastCameraState.rotation[2]) +
                Math.abs(state.rotation[3] - this.lastCameraState.rotation[3]);
            const zoomDiff = Math.abs(state.zoom - this.lastCameraState.zoom);

            if (rotDiff < 0.001 && zoomDiff < 1) return;
        }

        this.lastCameraState = state;
        // PlayerId mitschicken damit Empfänger wissen von wem die Kamera kommt
        this.safeEmit('camera:update', {
            ...state,
            playerId: this.playerId
        });

        // Throttle für nächstes Update
        this.cameraUpdateThrottle = setTimeout(() => {
            this.cameraUpdateThrottle = null;
        }, this.CAMERA_UPDATE_INTERVAL);
    }

    /**
     * Springt zur Kamera-Position des aktuell aktiven Spielers
     */
    syncToActivePlayer() {
        if (!this.game || !this.renderer) return;

        const currentPlayer = this.game.getCurrentPlayer();
        if (currentPlayer && currentPlayer.id !== this.playerId) {
            // Wir sind nicht dran - zur gespeicherten Position des aktiven Spielers springen
            this.renderer.restorePlayerCamera(currentPlayer.id);
        }
    }

    /**
     * Render-Loop starten
     */
    startRenderLoop() {
        this._renderLoopActive = true;
        let lastTime = performance.now();

        const animate = (currentTime) => {
            if (!this._renderLoopActive) return;

            const deltaTime = currentTime - lastTime;
            lastTime = currentTime;

            // WebGL Sphere rendern
            if (this.renderer) {
                this.renderer.render();
            }

            // Virus Overlay rendern (für Explosions-Animationen)
            if (this.virusRenderer && this.game) {
                this.virusRenderer.render(deltaTime);
            }

            requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
    }

    /**
     * Explosions-Animation triggern
     */
    triggerExplosion(fieldId) {
        if (this.renderer) {
            this.renderer.triggerExplosion(fieldId);
        }
    }

    /**
     * Holt aktuellen Spielzustand
     */
    getGameState() {
        if (!this.game) return null;

        // Spielerstatistiken aus aktuellen Feldern berechnen
        const playerStats = this.calculatePlayerStats();

        // Spieler serialisieren und mit aktuellen Stats anreichern
        const players = [...this.game.players.values()].map((p) => {
            const serialized = p.serialize();
            const stats = playerStats.get(p.id);
            if (stats) {
                serialized.fieldCount = stats.fieldCount;
                serialized.totalViruses = stats.totalViruses;
            }
            return serialized;
        });

        return {
            gameId: this.gameId,
            state: this.game.state,
            round: this.game.currentRound,
            currentPlayer: this.game.getCurrentPlayer(),
            isMyTurn: this.isMyTurn(),
            isHost: this.isHost(),
            players,
            slots: this.game.slots || [],
            canStart: this.game.canStartResult || { canStart: false },
            sphereStats: this.game.sphere.stats,
            turnTimeRemaining: this.timer.getRemaining(),
            turnTimeLimit: this.timer.turnTimeLimit
        };
    }

    /**
     * Berechnet Spielerstatistiken aus den aktuellen Feldern
     */
    calculatePlayerStats() {
        const stats = new Map();

        // Initialisiere Stats für alle Spieler
        for (const player of this.game.players.values()) {
            stats.set(player.id, { fieldCount: 0, totalViruses: 0 });
        }

        // Zähle Felder und Viren
        for (const field of this.game.sphere.fields.values()) {
            if (field.ownerId && stats.has(field.ownerId)) {
                const playerStats = stats.get(field.ownerId);
                playerStats.fieldCount++;
                playerStats.totalViruses += field.value || 0;
            }
        }

        return stats;
    }
}

export default MultiplayerClient;
